// Staff authentication hooks and route guard.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import React, { useEffect } from 'react';
import { apiFetch } from '@/lib/api';

export interface StaffMe {
  staff_id: number;
  login_name: string;
  username: string;
  email: string;
  last_login_at: number | null;
  session: {
    last_seen_at: number;
    expires_at: number;
    csrf_token_hash: string;
  };
  webauthn_count: number;
  totp_enrolled: boolean;
}

/**
 * TanStack Query hook — fetches GET /admin/auth/me.
 * Throws on non-ok so isError / isLoading states work correctly.
 */
export function useStaffMe(): UseQueryResult<StaffMe> {
  return useQuery<StaffMe>({
    queryKey: ['staff-me'],
    queryFn: async () => {
      const result = await apiFetch<StaffMe>('/admin/auth/me');
      if (!result.ok) {
        throw new Error(`Unauthorized: ${result.status}`);
      }
      return result.data;
    },
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Route guard component.
 * - Renders children when the staff session is authenticated.
 * - Shows a centered spinner while the session is being checked.
 * - Redirects to /login on error (e.g. 401).
 */
export function AuthGuard({ children }: { children: React.ReactNode }): React.ReactElement {
  const { isLoading, isError } = useStaffMe();
  const navigate = useNavigate();

  useEffect(() => {
    if (isError) {
      void navigate({ to: '/login' });
    }
  }, [isError, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-300 border-t-neutral-800"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (isError) {
    // Redirect is firing; render nothing while navigation happens.
    return <></>;
  }

  return <>{children}</>;
}
